import { create } from 'zustand'
import { supabase } from '@/config/supabase'
import { ROLES } from '@/config/constants'

const useAuthStore = create((set, get) => ({
  // ─── State ──────────────────────────────────────────────────────────────────
  user:          null,   // supabase auth.user
  profile:       null,   // profiles table row
  loading:       true,   // true during initial session check
  authLoading:   false,  // true during sign-in/sign-out action
  error:         null,

  // Phone OTP flow state
  phoneStep:     'enter_phone', // 'enter_phone' | 'enter_otp'
  pendingPhone:  null,

  // ─── Computed Helpers ────────────────────────────────────────────────────────
  isAuthenticated: () => !!get().user,
  isCustomer:  () => get().profile?.role === ROLES.CUSTOMER,
  isWorker:    () => get().profile?.role === ROLES.WORKER,
  isAdmin:     () => get().profile?.role === ROLES.ADMIN,

  // ─── Init: subscribe to Supabase auth state changes ─────────────────────────
  initialize: () => {
    supabase.auth.getSession().then(({ data: { session } }) => {
      if (session?.user) {
        set({ user: session.user })
        get().fetchProfile(session.user.id)
      } else {
        set({ loading: false })
      }
    })

    const { data: { subscription } } = supabase.auth.onAuthStateChange(
      async (event, session) => {
        if (event === 'SIGNED_IN' && session?.user) {
          set({ user: session.user })
          await get().fetchProfile(session.user.id)
        } else if (event === 'SIGNED_OUT') {
          set({ user: null, profile: null, loading: false, error: null })
        } else if (event === 'TOKEN_REFRESHED' && session?.user) {
          set({ user: session.user })
        }
      }
    )

    return () => subscription.unsubscribe()
  },

  // ─── Fetch Profile ───────────────────────────────────────────────────────────
  fetchProfile: async (userId) => {
    try {
      const timeout = new Promise((_, reject) =>
        setTimeout(() => reject(new Error('timeout')), 8000)
      )
      const query = supabase
        .from('profiles')
        .select('*')
        .eq('id', userId)
        .single()

      const { data, error } = await Promise.race([query, timeout])

      if (error) {
        if (error.code === 'PGRST116') {
          set({ profile: null, loading: false })
          return
        }
        throw error
      }

      set({ profile: data, loading: false, error: null })
    } catch (err) {
      set({ profile: null, loading: false })
    }
  },

  // ─── Update Profile (after profile creation form) ────────────────────────────
  updateProfile: async (updates) => {
    const { user } = get()
    if (!user) return { error: 'Not authenticated' }

    try {
      const { data, error } = await supabase
        .from('profiles')
        .upsert({ id: user.id, ...updates, updated_at: new Date().toISOString() })
        .select()
        .single()

      if (error) throw error
      set({ profile: data })
      return { data, error: null }
    } catch (err) {
      return { error: err.message }
    }
  },

  // ─── Google OAuth ─────────────────────────────────────────────────────────────
  signInWithGoogle: async () => {
    set({ authLoading: true, error: null })
    try {
      const { error } = await supabase.auth.signInWithOAuth({
        provider: 'google',
        options: {
          redirectTo: `${window.location.origin}/auth/callback`,
          queryParams: {
            access_type: 'offline',
            prompt: 'consent',
          },
        },
      })
      if (error) throw error
    } catch (err) {
      set({ authLoading: false, error: err.message })
    }
  },

  // ─── Phone OTP: Step 1 — Send OTP ────────────────────────────────────────────
  sendPhoneOtp: async (phone) => {
    set({ authLoading: true, error: null })
    try {
      const normalized = phone.startsWith('0')
        ? '+63' + phone.slice(1)
        : phone.startsWith('+63')
          ? phone
          : '+63' + phone

      const { error } = await supabase.auth.signInWithOtp({
        phone: normalized,
      })
      if (error) throw error

      set({
        authLoading: false,
        phoneStep:   'enter_otp',
        pendingPhone: normalized,
      })
      return { error: null }
    } catch (err) {
      set({ authLoading: false, error: err.message })
      return { error: err.message }
    }
  },

  // ─── Phone OTP: Step 2 — Verify OTP ──────────────────────────────────────────
  verifyPhoneOtp: async (token) => {
    const { pendingPhone } = get()
    if (!pendingPhone) return { error: 'No pending phone number' }

    set({ authLoading: true, error: null })
    try {
      const { data, error } = await supabase.auth.verifyOtp({
        phone: pendingPhone,
        token,
        type: 'sms',
      })
      if (error) throw error

      set({ authLoading: false, phoneStep: 'enter_phone', pendingPhone: null })
      return { data, error: null }
    } catch (err) {
      set({ authLoading: false, error: err.message })
      return { error: err.message }
    }
  },

  // ─── Reset Phone OTP flow ─────────────────────────────────────────────────────
  resetPhoneStep: () => {
    set({ phoneStep: 'enter_phone', pendingPhone: null, error: null })
  },

  // ─── Sign Out ─────────────────────────────────────────────────────────────────
  signOut: async () => {
    set({ authLoading: true })
    await supabase.auth.signOut()
    set({ user: null, profile: null, authLoading: false, error: null })
  },

  // ─── Clear Error ──────────────────────────────────────────────────────────────
  clearError: () => set({ error: null }),
}))

export default useAuthStore
